// Kill Sheet Wizard — session storage layer (P4).
//
// Holds in-progress and saved kill sheets. Two-tier:
//   - DRAFT (single global, in-memory + sessionStorage) for the wizard.
//   - SAVED LIBRARY  (localStorage rsc.killsheets.v1) — Pro feature.
//
// Snapshot integrity: when a kill sheet is saved or finalized, the full
// resolved profile payloads are embedded inside `session.well/bha/csg/mud/pump`
// so the kill sheet remains valid even if the source profiles are later
// edited or deleted (per user req #4).
//
// Drill Pipe geometry snapshot (Sep 2026 root-cause correction, ticket
// 255586): `session.drillPipe` holds the Drill Pipe OD/ID resolved from the
// canonical Engineering Setup (see lib/killsheet/drillPipeResolver.js). It
// is `null` while drafting (live resolution) and becomes a `frozen: true`
// snapshot when the sheet is created / saved / printed, so a historical
// sheet never changes when the live Drill String is edited later.

import {
  getLatest, getByVersion,
} from "@/lib/profiles/storage";
import { scoped, scopedSession } from "@/lib/accountScope";
import { resolveDrillPipeGeometry } from "./drillPipeResolver";

const DRAFT_KEY = "rsc.killsheet.draft.v1";
// P0 fix — this legacy wizard used to share `rsc.killsheets.v1` with the
// canonical store in `lib/killsheet/storage.js`, writing an incompatible
// record shape into the same array. Repointed to its own key. Existing
// records already saved under `rsc.killsheets.v1` are left exactly where
// they are — nothing is migrated or reshaped.
const SAVED_KEY = "rsc.killsheets.legacy.v1";

const _safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

export function loadDraft() {
  return _safeParse(scopedSession.getItem(DRAFT_KEY), null);
}
export function saveDraft(session) {
  scopedSession.setItem(DRAFT_KEY, JSON.stringify(session));
}
export function clearDraft() {
  scopedSession.removeItem(DRAFT_KEY);
}

/**
 * Clone a kill-sheet session (P2 v5).
 *
 * Preserves: all bound profiles (well, bha, casing, mud, pump, opstate),
 * variant, units, refs map, capacities, lengths, general config.
 *
 * Resets: id (fresh), date (today), kickData (sidpp/sicp/pitGain/kickVolume),
 * stepDown working data, any time-sensitive in-progress entries, the frozen
 * Drill Pipe geometry snapshot and the creation marker (a clone is a NEW
 * sheet and resolves against the then-current active Drill String).
 *
 * Audit: writes `_clonedFrom = { id, label, at }` on the new session so the
 * editor can render a provenance pill and the supervisor can reason about
 * scenario lineage. If the source itself was cloned, that lineage is
 * preserved as `_clonedFrom._parent` (one level — kill sheets are not
 * intended to be deeply chained).
 */
export function cloneSession(source) {
  if (!source || typeof source !== "object") {
    throw new Error("cloneSession: source must be a session object");
  }
  const copy = JSON.parse(JSON.stringify(source));
  copy.id   = `ks_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  copy.date = new Date().toISOString().slice(0, 10);
  // Wipe kick / time-sensitive inputs.
  copy.kickData = { sidpp: "", sicp: "", pitGain: "", kickVolume: "" };
  if (copy.stepDown) delete copy.stepDown;
  // A clone is a new Kill Sheet: drop the source's frozen Drill Pipe geometry
  // and its creation marker so the copy resolves live and is "created" anew.
  delete copy.drillPipe;
  delete copy._createdAt;
  // Audit trace.
  copy._clonedFrom = {
    id:    source.id,
    label: source.label || source.well?.label || "(unlabeled)",
    at:    new Date().toISOString(),
    parent: source._clonedFrom || null,
  };
  return copy;
}

/** Returns a brand-new session shell for the given variant. */
export function newSession(variant) {
  return {
    id: `ks_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    variant: variant || "surface-vertical",
    units: "IMP",                       // Imperial default per req #5
    date: new Date().toISOString().slice(0, 10),
    refs: { well: null, bha: null, casing: null, mud: null, pump: null },
    well: null, bha: null, casing: null, mud: null,
    pump: { selectedIndex: 0, pumps: [] },
    // Drill Pipe geometry snapshot — null while drafting (resolved live from
    // the canonical Engineering Setup); frozen at creation. See header.
    drillPipe: null,
    capacities: {
      dpInternal: "", hwdpInternal: "", dcInternal: "",
      dpHwdpInOpenHole: "", dcInOpenHole: "",
      dpInCasing: "", dcInCasing: "",
    },
    lengths: {
      dp: "", hwdp: "", dc: "",
      dpHwdpOpenHole: "", dcOpenHole: "",
      dpInCasing: "", dcInCasing: "",
    },
    kickData: { sidpp: "", sicp: "", pitGain: "" },
  };
}

/**
 * Materialize a kind of profile selection: replaces session.refs[kind] with
 * { id, version } and embeds the resolved payload at session[kind].  This
 * is the snapshot-lock point.
 */
export function attachProfile(session, kind, profileId) {
  const row = getLatest(kind, profileId);
  if (!row) throw new Error(`Profile not found: ${kind}#${profileId}`);
  session.refs[kind] = { id: row.id, version: row.version };
  session[kind === "casing" ? "casing" : kind] = JSON.parse(JSON.stringify(row.payload));
  return session;
}

/**
 * Freeze the resolved Drill Pipe geometry onto the session (idempotent —
 * an already-frozen snapshot is never re-resolved). Called when a Kill Sheet
 * is created, saved to the library or handed to print, so the record keeps
 * exactly the geometry and derived values it was created with. Later edits
 * to the live Drill String never touch a frozen sheet; a NEW sheet
 * (newSession / cloneSession) resolves against the then-current Drill String.
 * Mutates and returns `session`.
 */
export function freezeDrillPipeGeometry(session) {
  if (!session || typeof session !== "object") return session;
  if (session.drillPipe && session.drillPipe.frozen) return session;
  const geom = resolveDrillPipeGeometry(session);
  session.drillPipe = { ...geom, frozen: true, frozenAt: new Date().toISOString() };
  return session;
}

/**
 * "Create Kill Sheet" — the explicit creation step in the editor. Freezes
 * the Drill Pipe geometry and stamps `_createdAt`. Kick data and every other
 * field stay editable afterwards; only the geometry snapshot is locked.
 * Mutates and returns `session`.
 */
export function createKillSheetFromSession(session) {
  if (!session || typeof session !== "object") return session;
  freezeDrillPipeGeometry(session);
  session._createdAt = new Date().toISOString();
  return session;
}

/** Saved (Pro) kill sheet library. */
export function listSavedKillSheets() {
  return _safeParse(scoped.getItem(SAVED_KEY), []);
}
export function saveKillSheet(session, output) {
  const list = listSavedKillSheets();
  // Persist a frozen copy so the library record never re-reads the live
  // Drill String (no-op when the editor already froze it at creation).
  const snapshot = freezeDrillPipeGeometry(JSON.parse(JSON.stringify(session)));
  list.unshift({
    id: session.id,
    savedAt: new Date().toISOString(),
    variant: session.variant,
    units: session.units,
    date: session.date,
    wellName: output?.wellSnapshot?.wellName || session.well?.label || "",
    session: snapshot,
    output: JSON.parse(JSON.stringify(output)),
  });
  scoped.setItem(SAVED_KEY, JSON.stringify(list));
  return list[0];
}

/** Resolve a snapshot ref via the profile storage layer (used to display
 *  read-only profile data inside a historical kill sheet). */
export function resolveSnapshotPayload(ref) {
  if (!ref || !ref.id) return null;
  // ref must encode kind too in practice — caller supplies it.
  return getByVersion(ref.kind, ref.id, ref.version);
}
